import { GoogleMap, Marker } from "@react-google-maps/api";
import { router } from "expo-router";
import { doc, getDoc } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { COUNTRIES } from "@/constants/countries";
import { NOTIFICATION_TYPES, SEVERITY } from "@/constants/notification-types";
import { useAuth } from "@/context/AuthContext";
import { firestore } from "@/firebase/web";
import { useGoogleMapsLoader } from "@/hooks/useGoogleMapsLoader.web";
import { useMapPosts } from "@/hooks/useMapPosts.web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MapPostsFilters } from "@/types/mapPostsFilters";
import { Post } from "@/types/post";
import { UserDocument } from "@/types/user";
import { cachePost } from "@/utils/postCache";
import {
  GEO_RESOLVE_TIMEOUT_MS,
  hasGeoPoint,
  LatLng,
  requestBrowserPosition,
} from "@/utils/geo.web";

import { MapFilterBar } from "./MapFilterBar.web";

type Props = {
  filters: MapPostsFilters;
  onFiltersChange: (next: MapPostsFilters) => void;
};

type Viewport = { center: LatLng; zoom: number };

const FALLBACK_VIEWPORT: Viewport = {
  center: { lat: 40.4168, lng: -3.7038 },
  zoom: 4,
};

type SeverityColors = { border: string; background: string };

const severityForEventType = (
  eventType: string | null | undefined,
): SeverityColors => {
  if (!eventType) return SEVERITY.gray;
  const meta = (
    NOTIFICATION_TYPES as Record<string, { severity: SeverityColors }>
  )[eventType];
  return meta?.severity ?? SEVERITY.gray;
};

const buildAnimatedMarkerSvg = (border: string): string =>
  `<svg xmlns='http://www.w3.org/2000/svg' width='44' height='44' viewBox='0 0 44 44'>` +
  `<circle cx='22' cy='22' r='10' fill='${border}' opacity='0.15'/>` +
  `<circle cx='22' cy='22' fill='none' stroke='${border}' stroke-width='2'>` +
  `<animate attributeName='r' values='6;18' dur='1.8s' repeatCount='indefinite'/>` +
  `<animate attributeName='opacity' values='0.65;0' dur='1.8s' repeatCount='indefinite'/>` +
  `</circle>` +
  `<circle cx='22' cy='22' fill='none' stroke='${border}' stroke-width='2'>` +
  `<animate attributeName='r' values='6;18' dur='1.8s' begin='0.9s' repeatCount='indefinite'/>` +
  `<animate attributeName='opacity' values='0.65;0' dur='1.8s' begin='0.9s' repeatCount='indefinite'/>` +
  `</circle>` +
  `<circle cx='22' cy='22' r='5' fill='${border}' stroke='#ffffff' stroke-width='2'/>` +
  `</svg>`;

const countryCenter = (countryCode: string | null): LatLng | null => {
  if (!countryCode) return null;
  const c = COUNTRIES.find((x) => x.value === countryCode);
  if (!c || !("coords" in c) || !c.coords) return null;
  return { lat: c.coords.latitude, lng: c.coords.longitude };
};

const postsCentroid = (
  visiblePosts: (Post & {
    location: { geoPoint: { latitude: number; longitude: number } };
  })[],
): LatLng | null => {
  if (visiblePosts.length === 0) return null;
  let lat = 0;
  let lng = 0;
  for (const p of visiblePosts) {
    lat += p.location.geoPoint.latitude;
    lng += p.location.geoPoint.longitude;
  }
  return { lat: lat / visiblePosts.length, lng: lng / visiblePosts.length };
};

export function PostsMap({ filters, onFiltersChange }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tintColor = useThemeColor({}, "tint");
  const backgroundColor = useThemeColor({}, "background");

  const { isLoaded, loadError } = useGoogleMapsLoader();

  const { posts, loading, error } = useMapPosts({ recency: filters.recency });

  const [browserPos, setBrowserPos] = useState<LatLng | null>(null);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [resolveElapsed, setResolveElapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    requestBrowserPosition().then((result) => {
      if (!cancelled) setBrowserPos(result.kind === "ok" ? result.position : null);
    });
    const timeoutId = setTimeout(() => {
      if (!cancelled) setResolveElapsed(true);
    }, GEO_RESOLVE_TIMEOUT_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setUserCountry(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(firestore, "users", user.uid))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() as UserDocument) : null;
        setUserCountry(data?.country ?? null);
      })
      .catch(() => {
        if (!cancelled) setUserCountry(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    for (const p of posts) {
      const c = p.location?.city;
      if (c) set.add(c);
    }
    return Array.from(set).sort();
  }, [posts]);

  const eventTypes = useMemo(() => {
    const set = new Set<string>();
    for (const p of posts) {
      if (p.eventType) set.add(p.eventType);
    }
    return Array.from(set).sort();
  }, [posts]);

  useEffect(() => {
    const patch: Partial<MapPostsFilters> = {};
    if (filters.city && !cities.includes(filters.city)) patch.city = null;
    if (filters.eventType && !eventTypes.includes(filters.eventType))
      patch.eventType = null;
    if (Object.keys(patch).length > 0) {
      onFiltersChange({ ...filters, ...patch });
    }
  }, [cities, eventTypes, filters, onFiltersChange]);

  const visiblePosts = useMemo(() => {
    return posts.filter(
      (
        p,
      ): p is Post & {
        location: { geoPoint: { latitude: number; longitude: number } };
      } => {
        if (!hasGeoPoint(p)) return false;
        if (filters.city && p.location?.city !== filters.city) return false;
        if (filters.eventType && p.eventType !== filters.eventType)
          return false;
        return true;
      },
    );
  }, [posts, filters.city, filters.eventType]);

  const geoReady = browserPos !== null || resolveElapsed;
  const isInitialLoading = loading || (!isLoaded && !loadError) || !geoReady;

  const [initialViewport, setInitialViewport] = useState<Viewport | null>(null);
  useEffect(() => {
    if (initialViewport !== null) return;
    if (isInitialLoading) return;
    if (browserPos) {
      setInitialViewport({ center: browserPos, zoom: 11 });
      return;
    }
    const country = countryCenter(userCountry);
    if (country) {
      setInitialViewport({ center: country, zoom: 5 });
      return;
    }
    const centroid = postsCentroid(visiblePosts);
    if (centroid) {
      setInitialViewport({
        center: centroid,
        zoom: visiblePosts.length > 30 ? 4 : 10,
      });
      return;
    }
    setInitialViewport(FALLBACK_VIEWPORT);
  }, [
    initialViewport,
    isInitialLoading,
    browserPos,
    userCountry,
    visiblePosts,
  ]);

  const goToPost = useCallback((postId: string) => {
    router.push({
      pathname: "/(tabs)/post/[postId]",
      params: { postId },
    });
  }, []);

  const iconBySeverityBorder = useMemo(() => {
    if (!isLoaded || typeof google === "undefined" || !google.maps) {
      return new Map<string, google.maps.Icon>();
    }
    const cache = new Map<string, google.maps.Icon>();
    for (const key of ["red", "amber", "blue", "gray"] as const) {
      const sev = SEVERITY[key];
      cache.set(sev.border, {
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
          buildAnimatedMarkerSvg(sev.border),
        )}`,
        scaledSize: new google.maps.Size(44, 44),
        anchor: new google.maps.Point(22, 22),
      });
    }
    return cache;
  }, [isLoaded]);

  const mapRef = useRef<google.maps.Map | null>(null);
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);
  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const filtersKey = `${filters.recency}|${filters.city ?? ""}|${filters.eventType ?? ""}`;
  const lastFiltersKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastFiltersKeyRef.current === null) {
      lastFiltersKeyRef.current = filtersKey;
      return;
    }
    if (lastFiltersKeyRef.current === filtersKey) return;
    if (loading) return;
    lastFiltersKeyRef.current = filtersKey;
    if (!mapRef.current || visiblePosts.length === 0) return;
    if (typeof google === "undefined" || !google.maps) return;
    const bounds = new google.maps.LatLngBounds();
    for (const p of visiblePosts) {
      bounds.extend({
        lat: p.location.geoPoint.latitude,
        lng: p.location.geoPoint.longitude,
      });
    }
    mapRef.current.fitBounds(bounds, 64);
  }, [filtersKey, loading, visiblePosts]);

  return (
    <ThemedView style={styles.container}>
      <MapFilterBar
        filters={filters}
        cities={cities}
        eventTypes={eventTypes}
        onChange={onFiltersChange}
      />
      <View style={[styles.mapWrapper, { backgroundColor }]}>
        {loadError || error ? (
          <View style={styles.overlay}>
            <ThemedText style={styles.message}>{t("MAP_ERROR")}</ThemedText>
            {error ? (
              <ThemedText style={styles.errorDetail}>{error}</ThemedText>
            ) : null}
          </View>
        ) : !initialViewport ? (
          <View style={styles.overlay}>
            <ActivityIndicator size="large" color={tintColor} />
            <ThemedText style={styles.message}>{t("MAP_LOADING")}</ThemedText>
          </View>
        ) : (
          <>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={initialViewport.center}
              zoom={initialViewport.zoom}
              options={{
                scrollwheel: true,
                gestureHandling: "greedy",
                mapTypeControl: false,
                rotateControl: false,
                cameraControl: false,
                streetViewControl: true,
                streetViewControlOptions: {
                  position: google.maps.ControlPosition.LEFT_TOP,
                },
                zoomControl: true,
                zoomControlOptions: {
                  position: google.maps.ControlPosition.LEFT_TOP,
                },
                fullscreenControl: true,
              }}
              onLoad={onMapLoad}
              onUnmount={onMapUnmount}
            >
              {visiblePosts.map((p) => {
                const severity = severityForEventType(p.eventType);
                return (
                  <Marker
                    key={p.id}
                    position={{
                      lat: p.location.geoPoint.latitude,
                      lng: p.location.geoPoint.longitude,
                    }}
                    icon={iconBySeverityBorder.get(severity.border)}
                    onClick={() => {
                      cachePost(p);
                      goToPost(p.id);
                    }}
                  />
                );
              })}
            </GoogleMap>
            {loading ? (
              <View pointerEvents="none" style={styles.emptyOverlay}>
                <ActivityIndicator size="small" color={tintColor} />
              </View>
            ) : visiblePosts.length === 0 ? (
              <View pointerEvents="none" style={styles.emptyOverlay}>
                <ThemedText style={styles.message}>
                  {posts.length === 0
                    ? t("MAP_NO_POSTS")
                    : t("MAP_NO_POSTS_WITH_LOCATION")}
                </ThemedText>
                <ThemedText style={styles.emptyDetail}>
                  {t("MAP_FETCHED_COUNT", { count: posts.length })}
                </ThemedText>
              </View>
            ) : null}
          </>
        )}
      </View>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mapWrapper: {
    flex: 1,
    position: "relative",
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  emptyOverlay: {
    position: "absolute",
    top: 16,
    left: 16,
    right: 16,
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.92)",
  },
  message: {
    textAlign: "center",
    fontSize: 14,
  },
  errorDetail: {
    textAlign: "center",
    fontSize: 12,
    paddingHorizontal: 24,
    opacity: 0.8,
  },
  emptyDetail: {
    textAlign: "center",
    fontSize: 12,
    opacity: 0.7,
    marginTop: 4,
  },
});

export default PostsMap;
